import { Box, Card, CardContent, Grid } from '@mui/material'
import { usePlanningStore } from '@planning/AppProvider'
import { CarrierVisitPlanningDateAxis } from '@planning/components/carrier-visit-planning/CarrierVisitPlanningDateAxis'
import { CarrierVisitPlanningHeader } from '@planning/components/carrier-visit-planning/CarrierVisitPlanningHeader'
import { useTranslate } from '@tolgee/react'
import { useMinimalsTheme } from '@tom-ui/ui'
import { PATHS } from '@tom-ui/utils'
import { observer } from 'mobx-react-lite'
import { useEffect } from 'react'
import { useNavigate } from 'react-router'
import { BerthLayout } from './Components/BerthLayout'
import { BerthPlanningPierSelection } from './Components/BerthPlanningPierSelection'
import { BerthVisualization } from './Components/BerthVisualization'
import { BerthPlanningViewStore } from './Stores/BerthPlanningViewStore'

interface Props {
  store: BerthPlanningViewStore
  headerActions?: JSX.Element
}
export const BerthPlanning = observer(({ store, headerActions }: Props) => {
  const theme = useMinimalsTheme()
  const { t } = useTranslate()
  const { vesselVisitPlanningStore } = usePlanningStore()
  const navigate = useNavigate()

  useEffect(() => {
    const gettingBethViewSize = () => {
      const mainElement = document.querySelector(`#berth-visualization`)

      if (mainElement) {
        store.setBerthViewTotalWidth(Math.floor(mainElement.getBoundingClientRect().width))
      }
    }

    const handleResize = () => {
      gettingBethViewSize()
    }

    gettingBethViewSize()
    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [store])

  useEffect(() => {
    const { selectedPierId } = store

    if (!selectedPierId && store.piers.length > 0) {
      store.setSelectPierId(store.piers[0].id)
    }
  }, [store])

  return (
    <Card sx={{ overflowY: 'auto' }}>
      {headerActions && (
        <Box
          display='flex'
          justifyContent='flex-end'
          sx={{
            p: 2,
            borderBottom: `1px solid ${theme.palette.grey[100]}`,
          }}
        >
          {headerActions}
        </Box>
      )}
      <CardContent>
        <Grid container sx={{ paddingBottom: 2 }}>
          <Grid
            sx={{
              borderBottom: `1px solid ${theme.palette.divider}`,
            }}
            size={12}
          >
            <CarrierVisitPlanningHeader title={t('berthPlan', 'Berth plan')} store={store} />
          </Grid>

          <Grid
            sx={{
              borderRight: `1px solid ${theme.palette.divider}`,
              borderBottom: `1px solid ${theme.palette.divider}`,
            }}
            size={2}
          >
            <BerthPlanningPierSelection
              options={store.piersWithBerths}
              selectedPierId={store.selectedPierId}
              onChange={(id: string) => store.setSelectPierId(id)}
            />
          </Grid>

          <Grid
            sx={{
              borderBottom: `1px solid ${theme.palette.divider}`,
            }}
            size={10}
          >
            <CarrierVisitPlanningDateAxis
              itemCount={store.daysVisualization}
              startDate={store.startDate}
              pixelPerHour={store.pixelPerHour}
            />
          </Grid>

          <Grid
            sx={{
              borderRight: `1px solid ${theme.palette.divider}`,
            }}
            size={2}
          >
            <BerthLayout
              berths={store.berths}
              pixelPerWaterMark={store.pixelPerWaterMark}
              startWaterMark={store.startWaterMark}
              berthViewTotalWidth={store.berthViewTotalWidth}
            />
          </Grid>
          <Grid id='berth-visualization' overflow='hidden' size={10}>
            <BerthVisualization
              pixelPerHour={store.pixelPerHour}
              visits={store.plannedVisits}
              onEdit={
                store.isViewMode
                  ? (id: number) => navigate(`${PATHS.visits.vesselPlanning.url}/${id}`)
                  : undefined
              }
              isViewMode={store.isViewMode}
            />
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  )
})
